import { join, orderBy, some, uniq } from 'lodash';
import {
  checkTextWithSuggestions,
  initGrammalecte,
  initSpellchecker,
  nspellCheckWord,
} from '../../../../../utils/grammalecteClient';
import { removePunctuation } from '../utils/functions';

class Grammalecte {
  ignoreWords = [];
  ignoreSuggests = [];
  lang = 'fr';
  isProcessing = false;

  setLang(lang) {
    switch (lang) {
      case 'fr':
      case 'fr-FR':
      case 'fr-CA':
      case 'fr-BE':
      case 'fr-CH':
        this.lang = 'fr';
        break;

      case 'en':
      case 'en-GB':
      case 'en-US':
      case 'en-CA':
        this.lang = 'en';
        break;

      default:
        this.lang = null;
        break;
    }

    this.init();
  }

  async init() {
    if (!this.lang) {
      return;
    }

    if (this.lang === 'en') {
      await initSpellchecker();
      return;
    }

    await initGrammalecte();
  }

  stop() {
    if (this.isProcessing) {
      this.isProcessing = false;
    }
  }

  // ***************************************************************************************
  // convertWordsToSentences() permet de convertir les mots en phrases
  // ***************************************************************************************
  convertWordsToSentences(wordsToConvert) {
    const sentences = [];

    this.initWords(wordsToConvert);

    const indexLines = uniq(
      wordsToConvert
        .filter((x) => x.indexLine >= 0 && !x.parentIndexLine)
        .map((x) => x.indexLine)
    );

    for (let i = 0; i < indexLines.length; i++) {
      const indexLine = indexLines[i];
      const words = wordsToConvert.filter(
        (x) =>
          (x.indexLine === indexLine && !x.parentIndexLine) ||
          x.parentIndexLine === indexLine
      );

      if (!words.length) {
        continue;
      }

      sentences.push({
        indexLine,
        words: [...words],
        sentence: join(
          words.map((x) => x.text),
          ' '
        ),
      });
    }

    return sentences;
  }

  // ***************************************************************************************
  // convertWordsToOneSentence() permet de convertir les mots en une phrase
  // ***************************************************************************************
  convertWordsToOneSentence(indexLine, words) {
    this.initWords(words);

    const line = {
      indexLine,
      words: [...words],
      sentence: join(
        words.map((x) => x.text),
        ' '
      ),
    };
    return line;
  }

  // ***************************************************************************************
  // initWords() permet de créer l'objet lettersToCheck pour chaque mot
  // ***************************************************************************************
  initWords(words) {
    let indexLetterSentence = 0;

    words.forEach((word) => {
      if (word.newline) {
        indexLetterSentence = 0;
      }

      word.suggests = [];

      word.lettersToCheck = [];
      const text = word.text;
      for (let i = 0; i < text.length; i++) {
        const char = text.charAt(i);
        word.lettersToCheck.push({
          index: i,
          letter: char,
          indexLetterSentence,
        });

        indexLetterSentence++;
      }

      const space = {
        index: text.length,
        letter: ' ',
        indexLetterSentence,
      };

      word.lettersToCheck.push(space);

      indexLetterSentence++;
    });
  }

  // ***************************************************************************************
  // checkOneLine() permet de vérifier une ligne
  // ***************************************************************************************
  /* async checkOneLine(line) {
    try {
      const result = await checkTextWithSuggestions(line);
      return result;
    } catch (error) {
      console.error('Erreur lors de la vérification de la ligne:', error);
      return null;
    }
  }*/

  // ***************************************************************************************
  // checkLines() permet de vérifier plusieurs lignes, on envoie le texte ensemble
  // ***************************************************************************************
  async checkLines(linesToCheck) {
    const results = [];
    this.isProcessing = true;

    for (const line of linesToCheck) {

      if (!this.isProcessing) {
        break;
      }
      
      try {
        const sentence = line.sentence.replaceAll(`'`, '’');

        const result = await checkTextWithSuggestions(sentence);

        // Traiter les résultats et les associer aux mots
        result.forEach((para) => {
          // Associer les erreurs aux mots de la ligne
          if (
            para.aSpellErrWithSuggestions &&
            para.aSpellErrWithSuggestions.length > 0
          ) {
            para.aSpellErrWithSuggestions.forEach((error) => {
              // Trouver le mot correspondant dans sentence.words
              const matchingWord = line.words.find(
                (word) =>
                  word.text === error.sValue || word.text.includes(error.sValue)
              );

              if (matchingWord) {
                if (
                  this.ignoreWords.includes(
                    removePunctuation(matchingWord.text.toLowerCase())
                  )
                ) {
                  return;
                }

                if (some(this.ignoreSuggests, matchingWord)) {
                  return;
                }

                matchingWord.suggests =
                  orderBy(error.suggestions, (x) => x) || [];
                matchingWord.hasError = true;
                matchingWord.errorType = 'spelling';
              }
            });
          }

          if (para.aGrammErr && para.aGrammErr.length > 0) {
            // Gérer les erreurs grammaticales
            para.aGrammErr.forEach((error) => {
              const matchingWord = line.words.find((x) =>
                x.lettersToCheck.find(
                  (x) =>
                    x.indexLetterSentence >= error.nStart &&
                    x.indexLetterSentence <= error.nEnd
                )
              );

              if (matchingWord) {
                if (
                  this.ignoreWords.includes(
                    removePunctuation(matchingWord.text.toLowerCase())
                  )
                ) {
                  return;
                }

                if (some(this.ignoreSuggests, matchingWord)) {
                  return;
                }

                const suggest = {
                  suggests: orderBy(error.aSuggestions, (x) => x),
                  start: error.nStart,
                  end: error.nEnd,
                  message: error.sMessage,
                };

                matchingWord.suggests = [suggest];
                matchingWord.hasError = true;
                matchingWord.errorType = 'grammar';
              }
            });
          }
        });

        results.push({
          sentence: line,
          result,
          processed: true,
        });
      } catch (error) {
        console.error(
          'Erreur lors de la vérification de la phrase :',
          line.sentence,
          error
        );
        results.push({
          sentence: line,
          result: null,
          error: error.message,
          processed: false,
        });
      }
    }

    this.isProcessing = false;
    return results;
  }

  async checkWordsEnglish(wordsToCheck) {
    await initSpellchecker();

    for (const wordToCheck of wordsToCheck) {
      if (wordToCheck.suggests) {
        wordToCheck.suggests = [];
      }

      const wordsSplitted = wordToCheck.text.split(' ');

      for (const word of wordsSplitted) {

        if (!this.isProcessing) {
          break;
        }

        const wordWithoutPunctuation = removePunctuation(word);

        const result = nspellCheckWord(wordWithoutPunctuation);

        if (result.correct === false) {
          if (
            this.ignoreWords.includes(
              removePunctuation(wordWithoutPunctuation.toLowerCase())
            )
          ) {
            return;
          }

          if (some(this.ignoreSuggests, wordWithoutPunctuation)) {
            return;
          }

          const startOffset = wordToCheck.text.indexOf(wordWithoutPunctuation);

          const suggest = {
            suggests: orderBy(result.suggestions, (x) => x),
            start: startOffset,
            end: startOffset + wordWithoutPunctuation.length,
          };

          if (!wordToCheck.suggests) {
            wordToCheck.suggests = [];
            wordToCheck.hasError = true;
            wordToCheck.errorType = 'spelling-en';
          }

          wordToCheck.suggests.push(suggest);
        }
      }
    }
  }

  // ***************************************************************************************
  // ignoreWord() permet d'ignorer le mot dans toutes les suggestions
  // ***************************************************************************************
  ignoreWord(text) {
    const textLc = text.toLowerCase();
    if (!this.ignoreWords.includes(textLc)) {
      this.ignoreWords.push(textLc);
    }
  }

  ignoreSuggestWord(word) {
    this.ignoreSuggests.push(word);
  }
}

export default Grammalecte;
